import React, { useState, useCallback, useEffect, useRef } from "react"
import PropTypes from "prop-types"
import { AnimatePresence, motion } from "framer-motion"
import useViewportHeight from "hooks/useViewportHeight"
import useMediaQuery from "hooks/useMediaQuery"
import useOnKeydown from "hooks/useOnKeydown"
import useArrowFocus from "hooks/useArrowFocus"
import useMergedRefs from "hooks/useMergedRefs"
import InvisibleButton from "components/buttons/InvisibleButton"
import theme from "styles/theme"

const useSectionObserver = (sections, onChange) => {
  const viewportHeight = useViewportHeight(500)
  const observerMargin = Math.floor(viewportHeight / 2)

  useEffect(() => {
    const sectionData = sections.map(({ _key, title }) => ({
      id: `section-${_key}`,
      title,
    }))

    const handleIntersection = entries => {
      entries.forEach(entry => {
        if (entry.isIntersecting) {
          onChange(sectionData.find(({ id }) => id === entry.target.id).title)
        }
      })
    }

    const observer = new window.IntersectionObserver(handleIntersection, {
      rootMargin: `-${observerMargin - 1}px 0px -${observerMargin}px 0px`,
      threshold: 0,
    })

    sectionData.forEach(({ id }) => {
      const sectionElement = document.getElementById(id)
      sectionElement && observer.observe(sectionElement)
    })

    return () => {
      observer.disconnect()
    }
  })
}

const AnchorNav = ({ sections }) => {
  const closeButtonRef = useRef()
  const sectionIndicatorRef = useRef()
  const [isOpen, setOpen] = useState(false)
  const [activeSection, setActiveSection] = useState("Overview")

  const open = useCallback(() => {
    setOpen(true)

    // When opening, set the focus on the close button
    window.setTimeout(() => {
      closeButtonRef?.current?.focus()
    }, 250)
  }, [])

  const close = useCallback(() => {
    setOpen(prev => {
      // When closing, set the focus on the section indicator, but only if the
      // section selector is open (i.e. when the section selector has focus)
      if (prev) {
        sectionIndicatorRef?.current?.focus()
      }

      return false
    })
  }, [])

  useSectionObserver(sections, setActiveSection)

  // Support arrow keys to go between section selector and back-to-top btn
  const arrowContainerRef = useArrowFocus(["ArrowLeft", "ArrowRight"])

  // Close the section selector on ESC keypress
  const escContainerRef = useRef()
  useOnKeydown("Escape", close, escContainerRef)

  // Since we have two different refs for the container, we need to merge them
  const containerRef = useMergedRefs(arrowContainerRef, escContainerRef)

  return (
    <div
      ref={containerRef}
      css={{
        display: "grid",
        gridTemplateColumns: "248px 1fr 248px",
        alignItems: "stretch",
        position: "sticky",
        bottom: -2,
        width: "100%",
        borderBottom: `2px solid ${theme.lightGray}`,
        background: theme.white,
        filter: "drop-shadow(3px -4px 2px rgba(0, 0, 0, 0.10))",
        userSelect: "none",
        zIndex: 5,
        [theme.tabletSmall]: {
          gridTemplateColumns: "1fr",
        },
      }}
    >
      <div css={anchorNavWing}>Current Section</div>

      <div css={{ position: "relative" }}>
        {/* Shows the current section and opens the menu when clicked */}
        <SectionIndicator
          ref={sectionIndicatorRef}
          activeSection={activeSection}
          isOpen={isOpen}
          onRequestOpen={open}
        />

        {/* Renders selection menu when open; hidden otherwise */}
        <SectionSelector
          sections={sections}
          isOpen={isOpen}
          onRequestClose={close}
          closeButtonRef={closeButtonRef}
        />
      </div>

      <InvisibleButton
        css={{
          ...anchorNavWing,
          borderLeft: `2px solid ${theme.lightGray}`,
          transition: "color 250ms, background-color 250ms, border-color 250ms",
          position: "relative",
          cursor: "pointer",
          background: theme.nearWhite,
          marginLeft: -2,
          ":hover": {
            color: theme.midBlue,
            backgroundColor: theme.blue20,
            borderLeftColor: theme.midBlue,
            zIndex: 4,
          },
        }}
        onClick={() => {
          window.scrollTo({ top: 0, behavior: "smooth" })
        }}
      >
        Back to Top
        <svg css={{ marginLeft: 18 }} width="14" viewBox="0 0 14 18">
          <path
            stroke="currentColor"
            strokeLinecap="square"
            strokeWidth="1.5"
            d="M7.02 16.93V2.61M6.95 1.95L1.78 7.12M7.13 1.9l5.17 5.17"
          />
        </svg>
      </InvisibleButton>
    </div>
  )
}

export default AnchorNav

AnchorNav.propTypes = {
  sections: PropTypes.arrayOf(
    PropTypes.shape({
      _key: PropTypes.string.isRequired,
      title: PropTypes.string.isRequired,
    }).isRequired
  ).isRequired,
}

const SectionIndicator = React.forwardRef(
  ({ activeSection, isOpen, onRequestOpen }, ref) => (
    <InvisibleButton
      ref={ref}
      aria-haspopup
      aria-expanded={isOpen}
      onClick={onRequestOpen}
      css={{
        ...theme.bodyCopySm,
        borderWidth: "0 2px",
        borderStyle: "solid",
        borderColor: theme.lightGray,
        height: 70,
        display: "flex",
        gap: 16,
        width: "100%",
        alignItems: "center",
        justifyContent: "space-between",
        padding: "16px 24px",
        color: theme.darkGray,
        cursor: "pointer",
        transition: "color 250ms, border-color 250ms, background 250ms",
        svg: {
          color: theme.softGray,
          transition: "color 250ms",
        },
        ":after": {
          content: "''",
          position: "absolute",
          top: 0,
          bottom: 0,
          right: 0,
          width: 2,
          zIndex: 2,
          transition: "background 250ms",
        },
        ":hover": {
          color: theme.midBlue,
          borderColor: theme.midBlue,
          background: theme.blue20,
          ":after": {
            background: theme.midBlue,
          },
          svg: {
            color: theme.midBlue,
          },
        },
        [theme.tabletSmall]: {
          marginLeft: -2,
          marginRight: -2,
          ":hover:after": {
            content: "none",
          },
        },
      }}
    >
      {activeSection}
      <svg width="15" viewBox="0 0 15 11">
        <path fill="currentColor" d="M7.5 10.5L0 0h15L7.5 10.5z" />
      </svg>
    </InvisibleButton>
  )
)

SectionIndicator.displayName = "SectionIndicator"

SectionIndicator.propTypes = {
  activeSection: PropTypes.string.isRequired,
  onRequestOpen: PropTypes.func.isRequired,
  isOpen: PropTypes.bool.isRequired,
}

const SectionSelector = ({
  sections,
  isOpen,
  closeButtonRef,
  onRequestClose,
}) => {
  // const containerRef = useRef()
  const containerRef = useArrowFocus()

  return (
    <AnimatePresence>
      {isOpen && (
        <motion.div
          ref={containerRef}
          initial={{
            overflow: "hidden",
            maxHeight: 0,
          }}
          animate={{
            overflow: "auto",
            maxHeight: "calc(100vh - var(--headerHeightOffset))",
          }}
          exit={{
            overflow: "hidden",
            maxHeight: 0,
          }}
          transition={{
            type: "spring",
            stiffness: 300,
            damping: 30,
          }}
          css={{
            ...theme.bodyCopySm,
            position: "absolute",
            bottom: 0,
            left: 0,
            right: 0,
            zIndex: 5,
            "--headerHeightOffset": "0px",
            [theme.navBreak]: {
              "--headerHeightOffset": "var(--headerHeight)",
            },
            [theme.tabletSmall]: {
              marginLeft: -2,
              marginRight: -2,
            },
          }}
        >
          <div
            css={{
              background: theme.midBlue,
              color: theme.white,
              padding: 23,
              display: "flex",
              justifyContent: "space-between",
              alignItems: "center",
              position: "sticky",
              top: 0,
              zIndex: 3,
            }}
          >
            Jump to Section:
            <InvisibleButton
              ref={closeButtonRef}
              onClick={onRequestClose}
              css={{
                display: "block",
                cursor: "pointer",
                transition: "color 250ms",
                ":hover": {
                  color: theme.lightOrange,
                  textDecoration: "underline",
                },
              }}
            >
              Close Drawer
            </InvisibleButton>
          </div>

          <ol css={{ margin: 0, padding: 0, listStyle: "none" }}>
            {sections.map(section => (
              <li key={section._key}>
                <NavItem onRequestClose={onRequestClose} {...section} />
              </li>
            ))}
          </ol>
        </motion.div>
      )}
    </AnimatePresence>
  )
}

SectionSelector.propTypes = {
  isOpen: PropTypes.bool.isRequired,
  sections: PropTypes.array.isRequired,
  onRequestClose: PropTypes.func.isRequired,
  closeButtonRef: PropTypes.object.isRequired,
}

const anchorNavWing = {
  ...theme.t5md,
  padding: "16px 24px",
  color: theme.softGray,
  textAlign: "center",
  display: "flex",
  alignItems: "center",
  justifyContent: "center",
  [theme.tabletSmall]: {
    display: "none",
  },
}

const NavItem = ({ _key, title, onRequestClose }) => {
  const navBreak = useMediaQuery(theme.navBreak)

  const clickHandler = useCallback(() => {
    const target = document.querySelector(`#section-${_key}`)
    const headerOffset = navBreak
      ? document
          .querySelector("#gatsby-focus-wrapper header")
          .getBoundingClientRect().height
      : 0
    target &&
      window.scrollTo({
        top:
          target.getBoundingClientRect().top +
          window.pageYOffset -
          headerOffset,
        behavior: "smooth",
      })
    onRequestClose()
  }, [_key, onRequestClose, navBreak])

  return (
    <InvisibleButton
      key={_key}
      onClick={clickHandler}
      css={{
        display: "block",
        background: theme.white,
        color: theme.darkGray,
        padding: 23,
        position: "relative",
        width: "100%",
        border: `2px solid ${theme.lightGray}`,
        borderBottomWidth: 0,
        transition: "background-color 250ms, border-color 250ms",
        cursor: "pointer",
        ":last-child": {
          borderBottomWidth: 2,
          borderBottomColor: "transparent",
        },
        ":hover": {
          background: theme.blue20,
          borderColor: theme.midBlue,
          ":after": {
            opacity: 0,
          },
        },
        ":hover + &": {
          borderTopColor: theme.midBlue,
        },
        "& + &": {
          marginTop: -2,
        },
      }}
    >
      {title}
    </InvisibleButton>
  )
}

NavItem.propTypes = {
  _key: PropTypes.string.isRequired,
  title: PropTypes.string.isRequired,
  onRequestClose: PropTypes.func.isRequired,
}
